import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import { FormInput, FormSelect, FormTextarea } from '../FormField';
import { fetchPublicRegistry, PublicRegistryError, reservePublicGift } from '../../services/GiftRegistryService';
import { resolveMediaUrl } from '../../services/uploadService';
import { formatDateLong } from '../../utils/formatDate';
import { GIFT_KIND_LABELS, guestStatusText } from '../../utils/giftRegistry';
import type { PublicGiftItem, PublicRegistry } from '../../types/GiftRegistry';
import '../../styles/components/GiftRegistry.css';

type State =
  | { phase: 'loading' }
  | { phase: 'unavailable' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; registry: PublicRegistry };

/**
 * The guests' page: no sign-in, no sidebar, built for a phone. Shows the couple, the date and the list; a guest can reserve
 * an item with their name. It only ever shows "Reserved" or "Still wanted", never who reserved or what anything costs.
 */
const PublicGiftRegistryPage: React.FC<{ token: string }> = ({ token }) => {
  const [state, setState] = useState<State>({ phase: 'loading' });
  const [reserving, setReserving] = useState<PublicGiftItem | null>(null);
  const [thanks, setThanks] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      setState({ phase: 'ready', registry: await fetchPublicRegistry(token) });
    } catch (err) {
      if (err instanceof PublicRegistryError && err.status === 404) setState({ phase: 'unavailable' });
      else setState({ phase: 'error', message: err instanceof PublicRegistryError ? err.message : 'Something went wrong. Please try again.' });
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  // The page is private: ask search engines to skip it, and give the tab a plain title.
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Gift registry';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.title = previousTitle; meta.remove(); };
  }, []);

  const replaceItem = (updated: PublicGiftItem) =>
    setState(s => (s.phase === 'ready' ? { phase: 'ready', registry: { ...s.registry, items: s.registry.items.map(i => (i.id === updated.id ? updated : i)) } } : s));

  if (state.phase === 'loading') {
    return <main className="gr-public"><p className="gr-note" role="status">Loading the registry...</p></main>;
  }
  if (state.phase === 'unavailable') {
    return (
      <main className="gr-public">
        <div className="gr-public__panel" role="alert">
          <h1 className="gr-public__title">This link is not available</h1>
          <p className="gr-note">The registry may have been switched off, or the link has changed. Please ask the couple for their latest link.</p>
        </div>
      </main>
    );
  }
  if (state.phase === 'error') {
    return (
      <main className="gr-public">
        <div className="gr-public__panel" role="alert">
          <h1 className="gr-public__title">We could not load the registry</h1>
          <p className="gr-note">{state.message}</p>
          <Button variant="primary" onClick={() => void load()}>Try again</Button>
        </div>
      </main>
    );
  }

  const { couple, items } = state.registry;
  const date = couple.event_date ? formatDateLong(couple.event_date) : '';
  return (
    <main className="gr-public">
      <header className="gr-public__header">
        <p className="gr-public__eyebrow">Gift registry</p>
        <h1 className="gr-public__title gr-wrap">{couple.names || 'Our registry'}</h1>
        {date && <p className="gr-public__date">{date}</p>}
        <p className="gr-note">Choose something from the list and reserve it, so nobody doubles up.</p>
      </header>

      {thanks && <p className="gr-public__thanks" role="status">{thanks}</p>}

      {items.length === 0 ? (
        <div className="gr-public__panel"><p className="gr-note">There is nothing on the list yet. Please check back soon.</p></div>
      ) : (
        <ul className="gr-public__list">
          {items.map(item => (
            <li key={item.id} className="gr-public__item">
              {item.photo_url && <img className="gr-public__photo" src={resolveMediaUrl(item.photo_url)} alt={`Photo of ${item.title}`} loading="lazy" />}
              <div className="gr-public__body">
                <div className="gr-public__meta">
                  <span className="gr-chip">{GIFT_KIND_LABELS[item.kind]}</span>
                  {item.priority === 'high' && <span className="gr-chip gr-chip--high">Most wanted</span>}
                </div>
                <h2 className="gr-public__item-title gr-wrap">{item.title}</h2>
                {item.description && <p className="gr-public__desc gr-wrap">{item.description}</p>}
                {item.link_url && (
                  <a className="gr-link" href={item.link_url} target="_blank" rel="noopener noreferrer nofollow">
                    Take a look<span className="sr-only"> at {item.title} (opens in a new tab)</span>
                  </a>
                )}
                <div className="gr-public__foot">
                  <span className={`gr-status${item.status === 'reserved' ? ' gr-status--reserved' : ''}`}>{guestStatusText(item)}</span>
                  <Button variant="primary" disabled={item.status === 'reserved'} onClick={() => { setThanks(null); setReserving(item); }}
                    aria-label={item.status === 'reserved' ? `${item.title} is reserved` : `Reserve ${item.title}`}>
                    {item.status === 'reserved' ? 'Reserved' : 'Reserve'}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {reserving && (
        <ReserveDialog
          token={token}
          item={reserving}
          onClose={() => setReserving(null)}
          onDone={(updated, name) => {
            replaceItem(updated);
            setReserving(null);
            setThanks(`Thank you, ${name}! "${updated.title}" is reserved for you.`);
          }}
          onStale={updated => replaceItem(updated)}
        />
      )}
    </main>
  );
};

interface DialogProps {
  token: string;
  item: PublicGiftItem;
  onClose: () => void;
  onDone: (updated: PublicGiftItem, name: string) => void;
  /** Someone reserved first: the item with what is left now. */
  onStale: (updated: PublicGiftItem) => void;
}

const ReserveDialog: React.FC<DialogProps> = ({ token, item, onClose, onDone, onStale }) => {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [nameError, setNameError] = useState('');
  const [problem, setProblem] = useState('');
  const [saving, setSaving] = useState(false);
  const max = Math.max(item.remaining_quantity, 1);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (name.trim() === '') { setNameError('Please tell us your name.'); return; }
    setNameError('');
    setProblem('');
    setSaving(true);
    try {
      const updated = await reservePublicGift(token, item.id, { guest_name: name, note, quantity: Number(quantity) });
      onDone(updated, name.trim());
    } catch (err) {
      if (err instanceof PublicRegistryError && err.status === 409 && err.remaining !== null) {
        // Someone else got there first: show what is left instead of a bare error.
        const remaining = err.remaining;
        onStale({ ...item, remaining_quantity: remaining, status: remaining <= 0 ? 'reserved' : 'still_wanted' });
        setQuantity('1');
        setProblem(remaining <= 0 ? 'Sorry, someone has just reserved the last one.' : `Sorry, only ${remaining} ${remaining === 1 ? 'is' : 'are'} still wanted now.`);
        if (remaining <= 0) onClose();
      } else {
        setProblem(err instanceof PublicRegistryError && err.status !== 400 ? err.message : 'Please check your details and try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Reserve: ${item.title}`} size="small">
      <form className="gr-reserve" onSubmit={e => void submit(e)} noValidate>
        <FormInput label="Your name" type="text" value={name} required autoFocus autoComplete="name" maxLength={80}
          error={nameError} onChange={e => setName(e.target.value)} />
        {item.remaining_quantity > 1 && (
          <FormSelect label="How many will you give?" value={quantity}
            options={Array.from({ length: Math.min(max, 50) }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
            help={`${item.remaining_quantity} still wanted.`} onChange={e => setQuantity(e.target.value)} />
        )}
        <FormTextarea label="A note for the couple (optional)" rows={3} value={note} maxLength={500} onChange={e => setNote(e.target.value)} />
        <p className="gr-note">The couple will see your name and note. Other guests will only see that it is reserved.</p>
        {problem && <p className="gr-error" role="alert">{problem}</p>}
        <div className="gr-reserve__actions">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={saving}>Reserve</Button>
        </div>
      </form>
    </Modal>
  );
};

export default PublicGiftRegistryPage;
